import { useState } from "react";
import { BASE_URL } from '../apiConfig';
import { useAuth } from "../context/AuthProvider";
import { useLanguage } from "../context/LanguageProvider";
import { toast } from 'sonner';

export default function ChangePassword({userId}) {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const [passwordData, setPasswordData] = useState({
    new_password: "",
    confirm_password: "",
  })

  const handlePasswordChange = (e) => {
    const { name, value } = e.target
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handlePasswordSave = async () => {
    setPasswordLoading(true)
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(t('cors_change_password.passwords_mismatch'))
      setPasswordLoading(false)
      return
    }

    if (passwordData.new_password.length < 6) {
      toast.error(t('cors_change_password.password_too_short'))
      setPasswordLoading(false)
      return
    }

    try {
      const response = await fetch(`${BASE_URL}/api/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: userId,
          new_password: passwordData.new_password,
          new_password_confirmation: passwordData.confirm_password
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || t('cors_change_password.update_failed'))
      }

      toast.success(t('cors_change_password.update_success'))
      setPasswordData({
        new_password: "",
        confirm_password: "",
      })
      setIsChangingPassword(false)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setPasswordLoading(false)
    }
  }

  const handlePasswordCancel = () => {
    setPasswordData({
      new_password: "",
      confirm_password: "",
    })
    setIsChangingPassword(false)
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mt-8 max-xl:p-5">
      <div className="flex items-center justify-end">
        {!isChangingPassword && (
        <button
          onClick={() => setIsChangingPassword(true)}
          className="bg-[var(--color-primary-50)] text-[var(--color-primary-500)] cursor-pointer px-4 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-3"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
            />
          </svg>
          <span className="text-sm max-xl:text-xs">{t('cors_change_password.change_password')}</span>
        </button>
      )}
      </div>
      <div className="my-6">
        <div className="flex items-center gap-3">
          <div className="bg-red-100 p-3 rounded-lg ">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800">{t('cors_change_password.account_security')}</h2>
        </div>
      </div>

      {isChangingPassword ? (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">{t('cors_change_password.new_password')}</label>
            <input
              type="password"
              name="new_password"
              value={passwordData.new_password}
              onChange={handlePasswordChange}
              className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
              placeholder={t('cors_change_password.enter_password')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              {t('cors_change_password.confirm_password')}
            </label>
            <input
              type="password"
              name="confirm_password"
              value={passwordData.confirm_password}
              onChange={handlePasswordChange}
              className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
              placeholder={t('cors_change_password.confirm_placeholder')}
            />
          </div>
          <div className="flex gap-2 pt-4">
            <button
                onClick={handlePasswordSave}
                disabled={passwordLoading}
                className={`bg-green-500 cursor-pointer text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors duration-200 flex items-center gap-1 ${passwordLoading ? 'opacity-70' : ''}`}
              >
                {passwordLoading ? (
                    <>
                      <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>{t('cors_change_password.updating')}</span>
                    </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">{t('cors_change_password.update')}</span>
                  </>
                )}
              </button>
            <button
              onClick={handlePasswordCancel}
              className="bg-gray-500 cursor-pointer text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-600 transition-colors duration-200 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span className="text-sm">{t('cors_change_password.cancel')}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="bg-gray-200 p-3 rounded-lg">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">{t('cors_change_password.secure_password')}</h3>
              <p className="text-xs text-gray-600">{t('cors_change_password.password_protected')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
